import { AccessAction, Permission, PublicUser } from '@common/entities';
import { DateFormat, ICustomFormElementProps } from '@common/interfaces';
import { MPAlert, MPButton, MPTable } from '@components';
import { Checkbox, Collapse, Form, type TableProps } from 'antd';
type ColumnsType<T = any> = NonNullable<TableProps<T>['columns']>;
import dayjs from 'dayjs';
import EmailForm from './EmailForm';
import Styles from './Styles.module.scss';

export default function PermissionsTable() {
    const form = Form.useFormInstance();
    const permissions = Form.useWatch<Permission[]>(['accessManagement', 'permissions']);
    const handleDelete = (user: string) => {
        form.setFieldValue(
            ['accessManagement', 'permissions'],
            permissions.filter(item => item.user !== user)
        );
    };

    const columns: ColumnsType<Permission> = [
        {
            dataIndex: 'userInfo',
            key: 'user',
            title: 'User',
            align: 'center',
            render: (user: PublicUser) => `${user?.fullName || user?.userName} (${user?.email})`,
        },
        {
            dataIndex: 'permissions',
            title: 'Permissions',
            align: 'center',
            children: [AccessAction.READ, AccessAction.UPDATE, AccessAction.ACCESS, AccessAction.MANAGE].map(accessAction => ({
                title: PermissionLabelMap[accessAction],
                dataIndex: accessAction,
                align: 'center',
                width: 100,
                render: (_, record, i) => (
                    <Form.Item name={['accessManagement', 'permissions', i, 'access']}>
                        <PermissionSwitch accessAction={accessAction} />
                    </Form.Item>
                ),
            })),
        },
        {
            dataIndex: 'createdAt',
            title: 'Added At',
            align: 'center',
            render: (value: string) => dayjs(value).format(DateFormat.Default),
        },
        {
            dataIndex: 'action',
            title: 'Action',
            align: 'center',
            render: (_, record, i) => (
                <MPButton
                    type="link"
                    onClick={() => handleDelete(record.user)}
                >
                    Delete
                </MPButton>
            ),
        },
    ];

    return (
        <>
            <EmailForm />

            <Form.Item
                name={['accessManagement', 'permissions']}
                valuePropName="dataSource"
            >
                <MPTable
                    bordered={true}
                    columns={columns}
                    pagination={{ hideOnSinglePage: true }}
                    rowKey="user"
                />
            </Form.Item>
        </>
    );
}

const PermissionLabelMap: Partial<Record<AccessAction, string>> = {
    [AccessAction.MANAGE]: 'Manage',
    [AccessAction.READ]: 'List',
    [AccessAction.UPDATE]: 'Edit',
    [AccessAction.ACCESS]: 'View',
};

function PermissionSwitch({ value = [], onChange, accessAction }: PermissionSwitchProps) {
    const handleChange = (checked: boolean) => {
        if (checked) onChange?.([...value, accessAction]);
        else {
            onChange?.(value.filter(item => item !== accessAction));
        }
    };
    return (
        <Checkbox
            checked={value?.includes(AccessAction.MANAGE) || value?.includes(accessAction)}
            onChange={e => handleChange(e.target.checked)}
            disabled={accessAction != AccessAction.MANAGE && value?.includes(AccessAction.MANAGE)}
        />
    );
}

interface PermissionSwitchProps extends ICustomFormElementProps<Array<string>> {
    accessAction: AccessAction;
}
